import markdownit from "markdown-it"
import hljs from "highlight.js"
import htmx from "htmx.org"
import { getHtmlFromMarkdown } from "$utils/markdown"

declare global {
  interface Window {
    copyMarkdownToClipboard: (options: { 
      elementOrSelector: HTMLElement | string, 
      onSuccess?: () => void, 
      rawText?: string,
      htmlText?: string
    }) => Promise<void>
  }
}

export const markdownToHTML = (markdown: string): string => {
  const rendered = getHtmlFromMarkdown({ markdown })
  
  const wrapper = document.createElement("div")
  wrapper.innerHTML = rendered
  const tables = wrapper.querySelectorAll("table")

  tables.forEach(table => {
    clampTableCellContent(table)
    const wrapper = document.createElement("div")
    wrapper.classList.add("md-table-container")
    table.parentNode?.insertBefore(wrapper, table)
    wrapper.appendChild(table)
  })

  return wrapper.innerHTML
}

const clampTableCellContent = (table: HTMLTableElement) => {
  const rows = table.querySelectorAll("tr")
  rows.forEach(row => {
    const cells = row.querySelectorAll("td")
    cells.forEach(cell => {
      const newContainer = document.createElement("div")
      newContainer.classList.add("line-clamp-4")
      newContainer.appendChild(cell.firstChild as Node)
      cell.innerHTML = ""
      cell.appendChild(newContainer)
    })
  })
}

export const addPreCopyButtons = () => {
  const preElements = document.querySelectorAll("pre")
  preElements.forEach(preElement => {
    if (preElement.querySelector("button[data-value]")) {
      return
    }

    const copyButton = document.createElement("button")
    copyButton.setAttribute("type", "button")
    copyButton.setAttribute("data-value", preElement.innerText)
    copyButton.addEventListener("click", event => {
      window.copyToClipboard(event, preElement.innerText, () => {
        window.toggleCopyIcon(event.target as HTMLElement)
      })
    })

    const icon = document.createElement("div")
    icon.classList.add("icon")
    icon.style.cssText = "--icon-size:16;"

    icon.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" role="img">
        <use href="#copy"></use>
      </svg>
    `
    copyButton.appendChild(icon)
    preElement.appendChild(copyButton)
  })
}

export const formatMarkdown = () => {
  const containers = document.querySelectorAll("[data-md]")
  containers.forEach(container => {
    if (container.getAttribute("markdown-formatted") === "true") {
      return
    }
    container.innerHTML = markdownToHTML(container.innerHTML.replace(/&lt;/g, "<").replace(/&gt;/g, ">"))
    container.setAttribute("markdown-formatted", "true")
    htmx.process(container)
  })
}

window.copyMarkdownToClipboard = async({ elementOrSelector, onSuccess, rawText, htmlText }) => {
  const element = typeof elementOrSelector === "string" ? document.querySelector(elementOrSelector) as HTMLElement : elementOrSelector
  if (!element) {
    window.toast.error("Failed to copy text to clipboard")
    return
  }

  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([rawText ?? element.innerText], { type: "text/plain" }),
        "text/html": new Blob([htmlText ?? element.innerHTML], { type: "text/html" }),
      })
    ])
    onSuccess?.()
  } catch (err) {
    console.error(err)
    window.toast.error("Failed to copy text to clipboard")
  }
}
